import { useContext, useMemo } from 'react';
import useTableData, { ScorableItemRowHeader as TScorableItemRowHeader } from '../../hooks/useTableData';
import ScorableItemResultCell from './components/ScorableItemResultCell';
import ScorableItemRowHeader from './components/ScorableItemRowHeader';
import { Cell } from '@pelckmans/elementis/components/table';
import TimeIcon from '@pelckmans/elementis/icons/time';
import RetryIcon from '@pelckmans/elementis/icons/retry';
import DetailsIcon from '@pelckmans/elementis/icons/details';
import CalendarIcon from '@pelckmans/elementis/icons/calendar';
import { formatDate, formatDuration } from '../../../../../../../utils/formatDate';
import { useTranslation } from 'react-i18next';
import { CurrentOrPreviousGroup, Member } from '../../../../../../../api/groups';
import ModalContext from '../../../../../../../contexts/ModalContext';
import HistoryModal from '../../../history-modal/HistoryModal';
import { useResultsDataContext } from '../../../../context/ResultsDataContext';
import { useFilters } from '../../../../context/filter-context/FilterContext';

type Level = TScorableItemRowHeader['level'];

interface Props {
  scorableItem: TScorableItemRowHeader;
  level: Level;
  member: Member;
  group: CurrentOrPreviousGroup;
  getResult: ReturnType<typeof useTableData>['getResultForScorableMaterial'];
}

export default function SingleMemberScorableItemRow({ scorableItem, level, member, group, getResult }: Props) {
  const { t } = useTranslation();
  const { showModal } = useContext(ModalContext);
  const { scorableItems } = useResultsDataContext();
  const { selectedModule } = useFilters();

  const result = useMemo(
    () => getResult(member.id, scorableItem.materialId, scorableItem.scorableItemType),
    [getResult, member.id, scorableItem.materialId, scorableItem.scorableItemType],
  );

  function showHistoryModal() {
    showModal(
      <HistoryModal
        memberId={member.id}
        groupId={group.id}
        scorableItem={scorableItems.find(item => item.id === scorableItem.id.split('-')[0])!}
        id={scorableItem.id}
        moduleId={selectedModule!.id}
        isAssignment={scorableItem.scorableItemType === 'assignment'}
      />,
    );
  }

  if (!result) return null;

  const {
    score: { maximum, achieved },
    duration,
    completedBySystem,
    isZeroScore,
    achievedAt,
  } = result;

  const showAbsoluteScore = scorableItem.showPoints && maximum;

  return (
    <tr data-testid={`scorable-item-${scorableItem.materialId}-${scorableItem.scorableItemType}`}>
      <ScorableItemRowHeader scorableItem={scorableItem} level={level} />
      <ScorableItemResultCell
        memberId={member.id}
        materialId={scorableItem.materialId}
        type={scorableItem.scorableItemType}
        quantifiable={!scorableItem.unachievable}
        getResult={getResult}
      />
      <Cell>{maximum ? <Cell.Text title={`${Math.round((achieved / maximum) * 100)}%`} /> : null}</Cell>
      <Cell>{showAbsoluteScore ? <Cell.Text title={`${achieved} / ${maximum}`} /> : null}</Cell>
      <Cell>
        {result.duration ? (
          <Cell.Text
            title={formatDuration({ seconds: duration }, 'HH:mm')}
            startAdornment={<TimeIcon data-testid="time-icon" />}
          />
        ) : null}
      </Cell>
      <Cell>
        {!(completedBySystem && isZeroScore) && achievedAt ? (
          <Cell.Text startAdornment={<CalendarIcon data-testid="calendar-icon" />} title={formatDate(achievedAt)} />
        ) : null}
      </Cell>
      <Cell
        buttonGroupProps={{
          'aria-label': t('PAGES.RESULTS.PER_STUDENT.TABLE.SCORABLE_ITEM.SINGLE_STUDENT.ACTIONS.GROUP_A11Y_LABEL'),
          role: 'group',
          className: 'actions',
        }}
      >
        <Cell.Button
          startIcon={<RetryIcon data-testid="share-icon" />}
          disabled
          prominence="neutral"
          aria-label={t('PAGES.RESULTS.PER_STUDENT.TABLE.SCORABLE_ITEM.SINGLE_STUDENT.ACTIONS.SHARE_BUTTON_A11Y_LABEL')}
        />
        <Cell.Button
          startIcon={<DetailsIcon data-testid="open-sessions-modal" />}
          prominence="neutral"
          aria-label={t(
            'PAGES.RESULTS.PER_STUDENT.TABLE.SCORABLE_ITEM.SINGLE_STUDENT.ACTIONS.SESSION_MODAL_BUTTON_A11Y_LABEL',
          )}
          onClick={showHistoryModal}
        />
      </Cell>
    </tr>
  );
}
